import { useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Minimal accessible lightbox.
 * Props:
 *  - photos: string[]
 *  - index: number (current)
 *  - onClose: () => void
 *  - onIndexChange: (newIndex) => void
 */
export default function Lightbox({ photos, index, onClose, onIndexChange }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") onIndexChange?.((index + 1) % photos.length);
      if (e.key === "ArrowLeft")
        onIndexChange?.((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, photos.length, onClose, onIndexChange]);

  if (!photos?.length) return null;
  const cur = photos[index] || photos[0];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white/70 hover:text-white"
        onClick={onClose}
        aria-label="Fermer"
        data-testid="lightbox-close"
      >
        <X size={28} />
      </button>
      {photos.length > 1 && (
        <>
          <button
            type="button"
            className="absolute left-4 md:left-8 text-white/70 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange?.((index - 1 + photos.length) % photos.length);
            }}
            aria-label="Précédent"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={36} />
          </button>
          <button
            type="button"
            className="absolute right-4 md:right-8 text-white/70 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange?.((index + 1) % photos.length);
            }}
            aria-label="Suivant"
            data-testid="lightbox-next"
          >
            <ChevronRight size={36} />
          </button>
        </>
      )}
      <img
        src={cur}
        alt=""
        className="max-h-[88vh] max-w-[92vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {photos.length > 1 && (
        <div className="absolute bottom-6 left-0 right-0 text-center text-xs uppercase tracking-widest text-white/60">
          {index + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}
